import { notify } from "@/components/common/Notification";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { Loader2 } from "lucide-react";

/** API often returns `{ data: row }` or `{ result: row }`; form expects the row object. */
function unwrapDynamicRecord(raw: unknown): Record<string, unknown> | null {
  if (raw === null || raw === undefined || typeof raw !== "object") {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const inner = r.data ?? r.result ?? r;
  if (inner === null || inner === undefined || typeof inner !== "object") {
    return null;
  }
  return inner as Record<string, unknown>;
}

function getRecordPrimaryId(record: Record<string, unknown>): string {
  return String(record.id ?? record.pk ?? record._id ?? "").trim();
}

function recordMatchesId(
  record: Record<string, unknown> | null,
  recordId: string
): boolean {
  if (!record) {
    return false;
  }
  const loadedId = getRecordPrimaryId(record);
  if (!loadedId) {
    return true;
  }
  return loadedId === String(recordId).trim();
}

const SchemaFormAutoSubmit = forwardRef(
  (
    {
      form,
      schemaName,
      recordId,
    }: {
      form: any;
      schemaName?: string;
      recordId?: string;
    },
    ref
  ) => {
    const { data: schemaJson } = useQuery({
      queryKey: ["schema", schemaName],
      queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
      enabled: !!schemaName,
    });
    const isEditMode = !!recordId;

    const { data: recordInfo } = useQuery({
      queryKey: ["dynamic-model", schemaName, recordId],
      queryFn: () =>
        DynamicModuleService.getItemById(schemaName ?? "", recordId ?? ""),
      enabled: isEditMode && !!schemaName && !!recordId,
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
    });

    const recordData = useMemo(
      () =>
        unwrapDynamicRecord(recordInfo) ??
        (recordInfo as Record<string, unknown> | null),
      [recordInfo]
    );

    const recordReady =
      isEditMode &&
      !!recordId &&
      !!recordData &&
      recordMatchesId(recordData, recordId);

    const handleSave = async () => {
      await form.validate();
      const values = removeMetaFields(form.values);
      if (isEditMode) {
        // Check for changes before updating
        if (!recordData || !checkFormChanges(values, recordData, notify)) {
          return;
        }
        await DynamicModuleService.updateItem(
          schemaName ?? "",
          recordId ?? "",
          values
        );
      } else {
        await DynamicModuleService.createItem(schemaName ?? "", values);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    useEffect(() => {
      if (!recordReady || !recordData) {
        return;
      }
      const next = removeMetaFields(recordData);
      form.setInitialValues(next, "overwrite");
      form.setValues(next, "overwrite");
    }, [recordReady, recordData, form]);

    if (isEditMode && !recordReady) {
      return (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading school details...
          </p>
        </div>
      );
    }

    return (
      <SchemaFormRender
        form={form}
        schemaJson={schemaJson?.json ?? schemaJson}
      />
    );
  }
);

export default SchemaFormAutoSubmit;
